import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { contactAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Mail, Send, Trash2, Eye, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const AdminMessages = () => {
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await contactAPI.getAll();
      setMessages(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch messages",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (message) => {
    setSelectedMessage(message);
    setReplyText(message.reply || '');
    setIsDialogOpen(true);
    
    if (message.status === 'unread') {
      try {
        await contactAPI.update(message.id, { status: 'read' });
        setMessages(messages.map(m =>
          m.id === message.id ? { ...m, status: 'read' } : m
        ));
      } catch (error) {
        console.error('Failed to update message status');
      }
    }
  };

  const handleReply = async () => {
    try {
      await contactAPI.update(selectedMessage.id, { status: 'replied', reply: replyText });
      setMessages(messages.map(m =>
        m.id === selectedMessage.id
          ? { ...m, status: 'replied', reply: replyText }
          : m
      ));
      toast({
        title: "Reply sent",
        description: "Your reply has been saved"
      });
      setIsDialogOpen(false);
      setSelectedMessage(null);
      setReplyText('');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send reply",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await contactAPI.delete(id);
      setMessages(messages.filter(m => m.id !== id));
      toast({
        title: "Message deleted",
        description: "The message has been removed"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete message",
        variant: "destructive"
      });
    }
  };

  const filteredMessages = filter === 'all'
    ? messages
    : messages.filter(m => m.status === filter);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-light text-white">Contact Messages</h1>
          <div className="flex space-x-2">
            {['all', 'unread', 'read', 'replied'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 text-sm border transition-all duration-300 capitalize ${
                  filter === status
                    ? 'border-white bg-white text-black'
                    : 'border-white/20 text-gray-400 hover:border-white/60 hover:text-white'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12 border border-white/10">
              <Loader2 className="w-12 h-12 text-gray-500 mx-auto mb-4 animate-spin" />
              <p className="text-gray-400">Loading messages...</p>
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-12 border border-white/10">
              <Mail className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No messages found</p>
            </div>
          ) : (
            filteredMessages.map((message) => (
              <div
                key={message.id}
                className={`border p-4 transition-all duration-300 ${
                  message.status === 'unread'
                    ? 'border-green-500/30 bg-green-500/5'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-white">{message.name}</h3>
                      <span className={`text-xs px-2 py-1 ${
                        message.status === 'unread'
                          ? 'bg-green-500/20 text-green-400'
                          : message.status === 'replied'
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {message.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 mb-2">{message.email}</p>
                    <p className="text-gray-300 mb-2 line-clamp-2">{message.message}</p>
                    <p className="text-xs text-gray-500">{formatDate(message.timestamp)}</p>
                  </div>
                  
                  <div className="flex space-x-2 ml-4">
                    <Button
                      onClick={() => handleView(message)}
                      className="bg-white/10 text-white hover:bg-white/20 border border-white/20"
                    >
                      <Eye size={14} className="mr-2" />
                      View
                    </Button>
                    <Button
                      onClick={() => handleDelete(message.id)}
                      className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="bg-black border border-white/20 text-white max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-light">Message Details</DialogTitle>
            </DialogHeader>
            {selectedMessage && (
              <div className="space-y-6 mt-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1 uppercase tracking-wider">From</label>
                  <p className="text-white">{selectedMessage.name}</p>
                  <p className="text-sm text-gray-400">{selectedMessage.email}</p>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1 uppercase tracking-wider">Date</label>
                  <p className="text-gray-300">{formatDate(selectedMessage.timestamp)}</p>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-2 uppercase tracking-wider">Message</label>
                  <div className="p-4 border border-white/10 bg-white/5">
                    <p className="text-gray-300 leading-relaxed">{selectedMessage.message}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">Reply</label>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={6}
                    className="w-full bg-transparent border-white/10 focus:border-white text-white resize-none"
                    placeholder="Type your reply here..."
                  />
                </div>

                {selectedMessage.reply && (
                  <div className="p-4 border border-blue-500/20 bg-blue-500/5">
                    <label className="block text-xs text-blue-400 mb-2 uppercase tracking-wider">Previous Reply</label>
                    <p className="text-gray-300">{selectedMessage.reply}</p>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <Button
                    onClick={() => setIsDialogOpen(false)}
                    className="bg-white/10 text-white hover:bg-white/20 border border-white/20"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={handleReply}
                    className="bg-white text-black hover:bg-gray-200"
                    disabled={!replyText.trim()}
                  >
                    <Send size={16} className="mr-2" />
                    Send Reply
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminMessages;
